import React, { useState, useEffect } from "react";
import { ACTIVITY_STATUS, STATUS_MAPPING } from "../../common/Constants";
import FormRenderer, { ComponentMapper, FormTemplate, componentTypes } from "../../common/FormRenderer";
import { preventDefault } from "../../../utils/common";
import { validatorTypes } from "@data-driven-forms/react-form-renderer";
import { toast } from "react-toastify";

const PF_STYLES = `
.pf-modal-backdrop {
  position: fixed; inset: 0;
  background: rgba(15,23,42,0.45);
  z-index: var(--z-backdrop);
  display: flex; align-items: center; justify-content: center;
  opacity: 0; pointer-events: none;
  transition: opacity 0.18s ease; padding: 16px;
}
.pf-modal-backdrop.open { opacity: 1; pointer-events: auto; }
.pf-modal {
  background: #fff; border: 1px solid #F2F2F2; border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  width: 660px; max-width: 100%; max-height: calc(100vh - 32px);
  padding: 16px; display: flex; flex-direction: column; gap: 16px;
  overflow: hidden; transform: translateY(8px);
  transition: transform 0.18s ease;
  font-family: 'Montserrat';
}
.pf-modal-backdrop.open .pf-modal { transform: translateY(0); }
.pf-modal-head { display: flex; flex-direction: column; gap: 4px; flex-shrink: 0; }
.pf-modal-head-row { display: flex; gap: 4px; align-items: flex-start; }
.pf-modal-title { flex: 1; font-size: 20px; font-weight: 600; line-height: 1.2; color: #1E1E1E; }
.pf-modal-sub { font-size: 14px; font-weight: 400; line-height: 1.4; color: #757575; }
.pf-modal-close {
  flex-shrink: 0; width: 24px; height: 24px;
  background: none; border: none; cursor: pointer; padding: 0; color: #1E1E1E;
  display: inline-flex; align-items: center; justify-content: center;
}
.pf-modal-close:hover { opacity: 0.7; }
.pf-modal-close svg { width: 14px; height: 14px; display: block; }
.pf-divider { height: 1px; width: 100%; background: #E6E6E6; flex-shrink: 0; }
.pf-modal-body { display: flex; flex-direction: column; gap: 24px; overflow-y: auto; flex-shrink: 1; }
.pf-stats { display: flex; justify-content: space-between; align-items: stretch; padding: 0 100px; }
.pf-stat { display: flex; flex-direction: column; align-items: center; gap: 16px; white-space: nowrap; }
.pf-stat-num { font-size: 20px; font-weight: 600; line-height: 1.2; color: #1E1E1E; }
.pf-stat-label { font-size: 14px; font-weight: 400; line-height: 1.4; color: #757575; }
.pf-stat-sep { flex: 0 0 1px; align-self: center; height: 60px; width: 1px; background: #E6E6E6; }
.pf-fields { display: flex; flex-direction: column; gap: 12px; }
.pf-field { display: flex; flex-direction: column; gap: 9px; padding-top: 3px; }
.pf-field > label { font-size: 14px; font-weight: 500; line-height: 16px; color: #374151; }
.pf-field .ql-container { border-radius: 0 0 8px 8px; font-family: 'Montserrat'; font-size: 14px; }
.pf-field .ql-toolbar { border-radius: 8px 8px 0 0; border-color: #D1D5DB; }
.pf-field .ql-container.ql-snow { border-color: #D1D5DB; }
.pf-modal-foot { display: flex; gap: 24px; flex-shrink: 0; }
.pf-cancel, .pf-publish {
  flex: 1; height: 38px; padding: 8px 16px; border-radius: 8px; border: none;
  font-family: 'Montserrat'; font-size: 14px; font-weight: 500;
  line-height: 24px; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
}
.pf-cancel { background: #F3F4F6; color: #0F0F0F; }
.pf-cancel:hover { background: #E5E7EB; }
.pf-publish { background: #2965AD; color: #F3F3F3; }
.pf-publish:hover { background: #1e4f8a; }
`;

function PublishAgainModal({ onClose, onSubmit, selectedRows, isEmail, audit, auditType }: any) {
  const [counts, setCounts] = useState<any[]>([]);
  const [form, setForm] = useState<any>({});
  const [details, setDetails] = useState<any>({ hideButtons: true })

  const schema = auditType === "todo" ? {
    fields: [
      {
        component: componentTypes.TEXT_EDITOR,
        name: 'observations',
        label: 'Observations',
        placeholder: 'Enter Observations...',
        initialValue: audit.observations || ""
      },
      {
        component: componentTypes.TEXT_EDITOR,
        name: 'recommendations',
        label: 'Recommendations',
        placeholder: 'Enter Recommendations...',
        initialValue: audit.recommendations || ""
      },
    ]
  } : {
    fields: [
      {
        component: componentTypes.TEXT_FIELD,
        name: 'vendor_nature_of_work',
        label: 'Vendor Nature of Work',
        placeholder: 'Enter Vendor Nature of Work...',
        validate: [
          { type: validatorTypes.REQUIRED }
        ]
      },
      {
        component: componentTypes.TEXT_FIELD,
        name: 'employee_head_count',
        label: 'Employee Head Count',
        placeholder: 'Enter Employee Head Count...',
        validate: [
          { type: validatorTypes.REQUIRED }
        ]
      },
      {
        component: componentTypes.DATE_PICKER,
        name: 'audit_start_date',
        label: 'Audit Start Date',
        className: 'grid-col-100',
        range: false,
        clearable: true,
        onChange: onStartDateChange,
        validate: [
          { type: validatorTypes.REQUIRED }
        ]
      },
      {
        component: componentTypes.DATE_PICKER,
        name: 'audit_end_date',
        label: 'Audit End Date',
        className: 'grid-col-100',
        range: false,
        clearable: true,
        onChange: onEndDateChange,
        validate: [
          { type: validatorTypes.REQUIRED }
        ]
      },
      {
        component: componentTypes.TEXT_EDITOR,
        name: 'observations',
        label: 'Observations',
        placeholder: 'Enter Observations...',
        initialValue: audit.observations || ""
      },
      {
        component: componentTypes.TEXT_EDITOR,
        name: 'recommendations',
        label: 'Recommendations',
        placeholder: 'Enter Recommendations...',
        initialValue: audit.recommendations || ""
      },
    ]
  };

  function debugForm(_form: any) {
    setForm(_form);
    setDetails(_form.values);
  }

  function handleSubmit(e: any) {
    preventDefault(e);
    if (form.valid) {
      if (auditType === "todo") {
        const { recommendations, observations } = details;
        onSubmit(e, recommendations, observations);
      } else {
        const { recommendations, observations, vendor_nature_of_work, employee_head_count, audit_start_date, audit_end_date } = details;
        onSubmit(e, { recommendations, observations, vendor_nature_of_work, employee_head_count, audit_start_date, audit_end_date });
      }
    } else {
      toast.error("Please fill all the mandatory fields.");
    }
  }

  useEffect(() => {
    if (selectedRows) {
      const _counts: any = {};
      selectedRows.forEach((row: any) => {
        if (!_counts[row.status]) {
          _counts[row.status] = 0;
        }
        _counts[row.status] = _counts[row.status] + 1;
      });

      setCounts(Object.keys(_counts).map((key: any) => {
        return {
          key,
          label: key === ACTIVITY_STATUS.PENDING ? `${STATUS_MAPPING[key]} (No Audit Activities)` :  STATUS_MAPPING[key],
          count: _counts[key]
        }
      }));
    }
  }, [selectedRows]);

  function onStartDateChange(event: any) {
    setDetails({ ...details, audit_start_date: event })
  }

  function onEndDateChange(event: any) {
    setDetails({ ...details, audit_end_date: event })
  }

  return (
    <>
      <style>{PF_STYLES}</style>
      <div className="pf-modal-backdrop open">
        <div className="pf-modal" role="dialog" aria-modal="true">
          {/* Header */}
          <div className="pf-modal-head">
            <div className="pf-modal-head-row">
              <div className="pf-modal-title">
                {isEmail ? "Publish and trigger email" : "Publish and trigger without email"}
              </div>
              <button className="pf-modal-close" onClick={onClose} aria-label="Close">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="pf-modal-sub">
              There are <strong>{selectedRows.length} {selectedRows.length > 1 ? 'activities' : 'activity'}</strong> available for publishing.
            </div>
          </div>

          <div className="pf-divider"/>

          <div className="pf-modal-body">
            {/* Stats row */}
            <div className="pf-stats">
              <div className="pf-stat">
                <div className="pf-stat-num">{selectedRows.length}</div>
                <div className="pf-stat-label">Total Audits</div>
              </div>
              {counts.map((count: any) => (
                <React.Fragment key={count.key}>
                  <div className="pf-stat-sep"/>
                  <div className="pf-stat">
                    <div className="pf-stat-num">{count.count}</div>
                    <div className="pf-stat-label">{count.label}</div>
                  </div>
                </React.Fragment>
              ))}
            </div>

            {/* FormRenderer */}
            <div className="pf-fields">
              <FormRenderer
                FormTemplate={FormTemplate}
                initialValues={details}
                componentMapper={ComponentMapper}
                schema={schema}
                debug={debugForm}
              />
            </div>

            {/* Notification message */}
            <div style={{ fontSize: 14, color: '#1E1E1E', lineHeight: 1.6 }}>
              Are you sure you <strong>{isEmail ? "" : "don't"} want to notify all the vendor users, vendor admins, auditor users, auditor admins and customers</strong> tagged to these audits?
            </div>
          </div>

          <div className="pf-divider"/>

          {/* Footer */}
          <div className="pf-modal-foot">
            <button className="pf-cancel" onClick={onClose}>No</button>
            <button className="pf-publish" onClick={handleSubmit}>Yes</button>
          </div>
        </div>
      </div>
    </>
  )
}

export default PublishAgainModal;